import { createContext, useContext, useEffect, useState, ReactNode, useMemo } from 'react';
import { supabase } from '../lib/supabase';

// --- INTERFACES ---
interface AdminUser {
  id: string;
  email: string; 
  type: 'admin';
}

interface RegularUser {
  id: string;
  email: string;
  type: 'regular';
}

type User = AdminUser | RegularUser | null;
 
interface AuthContextType {
  user: User;
  loading: boolean;
  signIn: (username: string, password: string, userType?: 'admin' | 'regular') => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  updatePassword: (newPassword: string) => Promise<{ error: Error | null }>;
}

// --- CONTEXT SETUP ---
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// --- CONSTANTS ---
const SESSION_COOKIE_NAME = 'session_token';

// --- HELPER FUNCTIONS FOR COOKIES ---
export function getSessionToken(): string | null {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === SESSION_COOKIE_NAME) {
      return decodeURIComponent(value);
    }
  }
  return null;
}

export function setSessionToken(token: string) {
  const expiryDate = new Date();
  expiryDate.setDate(expiryDate.getDate() + 7);
  document.cookie = `${SESSION_COOKIE_NAME}=${encodeURIComponent(token)}; expires=${expiryDate.toUTCString()}; path=/; SameSite=Lax`;
}

export function clearSessionToken() {
  document.cookie = `${SESSION_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
}

// --- CORE SESSION LOGIC ---
async function validateSession(token: string): Promise<User | null> {
  try {
    if (!token || token.trim() === '') return null;

    const isAdminToken = token.includes('_') && token.split('_').length >= 3;
    
    if (isAdminToken) {
      const { data: session, error: sessionError } = await supabase
        .from('admin_sessions')
        .select('admin_id, expires_at, admins(id, email)')
        .eq('session_token', token)
        .maybeSingle();

      if (!sessionError && session) {
        if (new Date(session.expires_at) >= new Date()) {
          const admin = session.admins as any;
          return { id: admin.id, email: admin.email, type: 'admin' };
        } else {
          await supabase.from('admin_sessions').delete().eq('session_token', token);
        }
      }
    }

    const isJWT = token.split('.').length === 3;
    if (isJWT) {
      // NOTE: We rely on Supabase internal session handling for JWTs mostly, 
      // but this acts as a backup validator.
      try {
        const { data: { user }, error: authError } = await supabase.auth.getUser(token);
        if (!authError && user) {
          return { id: user.id, email: user.email || '', type: 'regular' };
        }
      } catch (jwtError) {
        console.error('JWT validation error:', jwtError);
        return null;
      }
    }
    return null;
  } catch (error) {
    console.error('Session validation error:', error);
    return null;
  }
}

async function createSession(userId: string, userType: 'admin' | 'regular'): Promise<string> {
  if (userType === 'admin') {
    const token = `${userId}_${Math.random().toString(36).substring(2)}_${Date.now()}`;
    await supabase.from('admin_sessions').insert({
      admin_id: userId,
      session_token: token
    });
    return token;
  } else {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token || '';
  }
}

// --- AUTH PROVIDER COMPONENT ---
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User>(null);
  const [loading, setLoading] = useState(true);

  // Helper to safely update user state without triggering unnecessary re-renders
  const safeSetUser = (newUser: User) => {
    setUser(prevUser => {
      // JSON stringify is a quick way to check deep equality for simple objects
      if (JSON.stringify(prevUser) === JSON.stringify(newUser)) {
        return prevUser;
      }
      return newUser;
    });
  };

  useEffect(() => {
    let mounted = true;

    const initSession = async () => {
      try {
        // 1. Check for Supabase Session (Regular User)
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (session?.user && !sessionError) {
          if (mounted) safeSetUser({ id: session.user.id, email: session.user.email || '', type: 'regular' });
        } else {
          // 2. Check for Admin Cookie
          const token = getSessionToken();
          if (token) {
            const validatedUser = await validateSession(token);
            if (mounted) {
              if (validatedUser) {
                safeSetUser(validatedUser);
              } else {
                clearSessionToken();
              }
            }
          }
        }
      } catch (error) {
        console.error('Error initializing session:', error);
        clearSessionToken();
        await supabase.auth.signOut({ scope: 'global' });
      } finally {
        if (mounted) setLoading(false);
      }
    };

    initSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return;

      try {
        if (event === 'SIGNED_OUT') {
          // Only clear if we were logged in as a regular user or explicit sign out
          const token = getSessionToken();
          // If we have an admin token, validate it before clearing everything
          if (token && token.includes('_')) {
             // Let the admin logic handle it, don't auto-clear based on Supabase event alone
          } else {
             safeSetUser(null);
             clearSessionToken();
          }
        } else if (session?.user) {
          safeSetUser({ id: session.user.id, email: session.user.email || '', type: 'regular' });
        }
      } catch (error) {
        console.error('Auth state change error:', error);
      } finally {
        setLoading(false);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (username: string, password: string, userType: 'admin' | 'regular' = 'regular') => {
    try {
      if (userType === 'admin') {
        const { data, error } = await supabase
          .from('admins')
          .select('*')
          .eq('email', username)
          .eq('password_hash', password)
          .maybeSingle();

        if (error) throw error;
        if (!data) return { error: new Error('Invalid username or password') };

        const sessionToken = await createSession(data.id, 'admin');
        setSessionToken(sessionToken);
        safeSetUser({ id: data.id, email: data.email, type: 'admin' });
        return { error: null };
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email: username, password });
        if (error) throw error;
        return { error: null };
      }
    } catch (error) {
      console.error('Sign in error:', error);
      return { error: error as Error };
    }
  };

  const signOut = async () => {
    try {
      if (user?.type === 'regular') {
        await supabase.auth.signOut();
      } else {
        const token = getSessionToken();
        if (token) {
          await supabase.from('admin_sessions').delete().eq('session_token', token);
          clearSessionToken();
        }
      }
      safeSetUser(null);
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const updatePassword = async (newPassword: string) => {
    try {
      if (!user) return { error: new Error('No user logged in') };
      if (user.type === 'regular') {
        const { error } = await supabase.auth.updateUser({ password: newPassword });
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('admins')
          .update({ password_hash: newPassword })
          .eq('id', user.id);
        if (error) throw error;
      }
      return { error: null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const value = useMemo(() => ({
    user,
    loading,
    signIn,
    signOut,
    updatePassword
  }), [user, loading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}